import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { Database } from '@/lib/database.types';

type CompositeComponent = Database['public']['Tables']['composite_product_components']['Row'];

export interface ComponentWithProduct extends CompositeComponent {
  component_product?: {
    id: string;
    name: string;
    price: number;
    cost: number | null;
    image_url: string | null;
  };
  raw_item?: {
    id: string;
    name: string;
    cost_per_unit: number | null;
    unit_of_measure: string;
    sku: string | null;
  };
}

export interface SaveComponentInput {
  componentProductId?: string;
  rawItemId?: string;
  quantity: number;
  quantityUnit?: string;
}

export interface VariantRecipeInput {
  variantId: string;
  components: SaveComponentInput[];
}

const COMPONENT_SELECT = `
  *,
  component_product:products!component_product_id(id, name, price, cost, image_url),
  raw_item:raw_inventory_items!raw_item_id(id, name, cost_per_unit, unit_of_measure, sku)
`;

export function useCompositeProducts(parentProductId: string | null, variantId?: string | null) {
  const queryClient = useQueryClient();

  // Fetch components for a composite product (optionally filtered by variant)
  const {
    data: components,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['composite-components', parentProductId, variantId ?? null],
    queryFn: async () => {
      if (!parentProductId) return [];

      let query = supabase
        .from('composite_product_components')
        .select(COMPONENT_SELECT)
        .eq('parent_product_id', parentProductId)
        .order('created_at');

      // If variantId is explicitly provided, filter by it
      // If variantId is undefined, fetch all (base + variant)
      // If variantId is null, fetch only base components
      if (variantId === null) {
        query = query.is('variant_id', null);
      } else if (variantId) {
        query = query.eq('variant_id', variantId);
      }
      // If variantId is undefined, no filter -- fetches all components

      const { data, error } = await query;
      if (error) throw error;
      return data as ComponentWithProduct[];
    },
    enabled: !!parentProductId,
  });

  // Fetch base components only (variant_id IS NULL) -- useful for composite_variant products
  const {
    data: baseComponents,
    isLoading: baseLoading,
  } = useQuery({
    queryKey: ['composite-components-base', parentProductId],
    queryFn: async () => {
      if (!parentProductId) return [];
      const { data, error } = await supabase
        .from('composite_product_components')
        .select(COMPONENT_SELECT)
        .eq('parent_product_id', parentProductId)
        .is('variant_id', null)
        .order('created_at');
      if (error) throw error;
      return data as ComponentWithProduct[];
    },
    enabled: !!parentProductId,
  });

  // Add component to composite product
  const addComponent = useMutation({
    mutationFn: async ({
      parentProductId,
      componentProductId,
      rawItemId,
      quantity,
      quantityUnit,
      variantId: compVariantId,
    }: {
      parentProductId: string;
      componentProductId?: string;
      rawItemId?: string;
      quantity: number;
      quantityUnit?: string;
      variantId?: string | null;
    }) => {
      const { data, error } = await supabase
        .from('composite_product_components')
        .insert({
          parent_product_id: parentProductId,
          component_product_id: componentProductId || null,
          raw_item_id: rawItemId || null,
          quantity,
          quantity_unit: quantityUnit || null,
          variant_id: compVariantId || null,
        })
        .select(COMPONENT_SELECT)
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['composite-components', parentProductId] });
      queryClient.invalidateQueries({ queryKey: ['composite-components-base', parentProductId] });
    },
  });

  // Update component quantity
  const updateComponent = useMutation({
    mutationFn: async ({
      componentId,
      quantity,
    }: {
      componentId: string;
      quantity: number;
    }) => {
      const { data, error } = await supabase
        .from('composite_product_components')
        .update({ quantity, updated_at: new Date().toISOString() })
        .eq('id', componentId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['composite-components', parentProductId] });
      queryClient.invalidateQueries({ queryKey: ['composite-components-base', parentProductId] });
    },
  });

  // Remove component
  const removeComponent = useMutation({
    mutationFn: async (componentId: string) => {
      const { error } = await supabase
        .from('composite_product_components')
        .delete()
        .eq('id', componentId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['composite-components', parentProductId] });
      queryClient.invalidateQueries({ queryKey: ['composite-components-base', parentProductId] });
    },
  });

  // Calculate total cost from components (including raw materials)
  const totalComponentCost = components?.reduce((sum, comp) => {
    let componentCost = 0;
    if (comp.component_product) {
      componentCost = comp.component_product.cost || comp.component_product.price || 0;
    } else if (comp.raw_item) {
      componentCost = comp.raw_item.cost_per_unit || 0;
    }
    return sum + componentCost * comp.quantity;
  }, 0) || 0;

  // Save multiple components at once (used after product creation)
  const saveComponents = useMutation({
    mutationFn: async ({
      parentProductId,
      components,
      variantId: saveVariantId,
    }: {
      parentProductId: string;
      components: SaveComponentInput[];
      variantId?: string | null;
    }) => {
      if (components.length === 0) return [];

      const { data, error } = await supabase
        .from('composite_product_components')
        .insert(
          components.map(comp => ({
            parent_product_id: parentProductId,
            component_product_id: comp.componentProductId || null,
            raw_item_id: comp.rawItemId || null,
            quantity: comp.quantity,
            quantity_unit: comp.quantityUnit || null,
            variant_id: saveVariantId || null,
          }))
        )
        .select(COMPONENT_SELECT);

      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['composite-components', variables.parentProductId] });
      queryClient.invalidateQueries({ queryKey: ['composite-components-base', variables.parentProductId] });
    },
  });

  // Save variant-specific recipes for multiple variants at once
  // This is used when creating a composite_variant product:
  // 1. First save base recipe (variant_id = null) via saveComponents
  // 2. Then save per-variant recipes with adjusted quantities via saveVariantRecipes
  const saveVariantRecipes = useMutation({
    mutationFn: async ({
      parentProductId,
      variantRecipes,
    }: {
      parentProductId: string;
      variantRecipes: VariantRecipeInput[];
    }) => {
      const allInserts: {
        parent_product_id: string;
        component_product_id: string | null;
        raw_item_id: string | null;
        quantity: number;
        quantity_unit: string | null;
        variant_id: string;
      }[] = [];

      for (const recipe of variantRecipes) {
        for (const comp of recipe.components) {
          allInserts.push({
            parent_product_id: parentProductId,
            component_product_id: comp.componentProductId || null,
            raw_item_id: comp.rawItemId || null,
            quantity: comp.quantity,
            quantity_unit: comp.quantityUnit || null,
            variant_id: recipe.variantId,
          });
        }
      }

      if (allInserts.length === 0) return [];

      const { data, error } = await supabase
        .from('composite_product_components')
        .insert(allInserts)
        .select(COMPONENT_SELECT);

      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['composite-components', variables.parentProductId] });
    },
  });

  return {
    components: components ?? [],
    baseComponents: baseComponents ?? [],
    isLoading,
    baseLoading,
    error,
    refetch,
    addComponent,
    updateComponent,
    removeComponent,
    saveComponents,
    saveVariantRecipes,
    totalComponentCost,
  };
}





